import type { DataGridPin } from "../columns/types";
import type { ResizeEdge } from "./columnResizer";

/** Half the header handle's width, so body borders grab like header ones. */
export const BORDER_HIT_SLOP = 4;

export interface ResizableColumn {
    id: string;
    pin: DataGridPin | false;
    canResize: boolean;
}

interface FindResizeBorderInput {
    pointerX: number;
    cellLeft: number;
    cellRight: number;
    columnId: string;
    /** Visible leaf columns in display order (start pins, center, end pins). */
    columns: readonly ResizableColumn[];
    /** Whether the next column's cell starts where this one ends (no filler between). */
    isNextAdjacent: boolean;
}

export interface ResizeBorder {
    columnId: string;
    edge: ResizeEdge;
}

/**
 * The column a pointer on a cell's left or right border resizes, matching the
 * header handles: a border is the end edge of the column before it, except an
 * end-pinned column's start border, which resizes that column (it grows
 * toward the scrolling area). `null` away from a border or when that column
 * can't resize.
 */
export const findResizeBorder = ({
    pointerX,
    cellLeft,
    cellRight,
    columnId,
    columns,
    isNextAdjacent,
}: FindResizeBorderInput): ResizeBorder | null => {
    const index = columns.findIndex((column) => column.id === columnId);
    if (index === -1) {
        return null;
    }
    const resizable = (
        column: ResizableColumn | undefined,
        edge: ResizeEdge,
    ): ResizeBorder | null =>
        column?.canResize ? { columnId: column.id, edge } : null;
    const current = columns[index];
    if (cellRight - pointerX <= BORDER_HIT_SLOP) {
        const next = columns[index + 1];
        return next?.pin === "end" && isNextAdjacent
            ? resizable(next, "start")
            : resizable(current, "end");
    }
    if (pointerX - cellLeft <= BORDER_HIT_SLOP) {
        return current.pin === "end"
            ? resizable(current, "start")
            : resizable(columns[index - 1], "end");
    }
    return null;
};

/** A pinned side's border while columns scroll under it. */
export interface PinEdge {
    side: DataGridPin;
    /** The pinned column on the border: the last start pin or the first end pin. */
    columnId: string;
    /** Where the border runs: the start pins' right, the end pins' left. */
    x: number;
    canResize: boolean;
}

export interface PinEdgeBorder extends ResizeBorder {
    /** The pinned side whose border this is. */
    side: DataGridPin;
}

/**
 * The pinned column a pointer on a shown pin border resizes, anywhere along
 * the border: the start pin grows from its end edge, the end pin from its
 * start edge. `null` off the border or when that column can't resize.
 */
export const findPinEdgeBorder = ({
    pointerX,
    edges,
}: {
    pointerX: number;
    edges: readonly PinEdge[];
}): PinEdgeBorder | null => {
    const edge = edges.find(
        (candidate) => Math.abs(pointerX - candidate.x) <= BORDER_HIT_SLOP,
    );
    if (!edge?.canResize) {
        return null;
    }
    return {
        columnId: edge.columnId,
        edge: edge.side === "start" ? "end" : "start",
        side: edge.side,
    };
};
